import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { ExportTrio, flattenSpec } from '../accounting/ExportTrio';
import { PeriodDrilldown } from './PeriodDrilldown';

const f = (n) => `SR ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Row = ({ label, value, testid, strong, onClick }) => (
  <div className={`flex justify-between gap-3 text-xs py-0.5 ${strong ? 'font-semibold' : ''} ${onClick ? 'cursor-pointer hover:text-primary transition-colors' : ''}`}
    data-testid={testid} onClick={onClick}>
    <span className={onClick ? 'underline decoration-dotted underline-offset-2' : 'text-muted-foreground'}>{label}</span><span className="tabular-nums">{f(value)}</span>
  </div>
);

const Box = ({ title, children, testid }) => (
  <div className="rounded-xl border border-border/60 p-3 bg-card" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{title}</div>
    {children}
  </div>
);

// drill requests: q = drilldown query, next = row → deeper drill
const D = {
  purchases: {
    title: 'Supplier purchases by supplier', q: { kind: 'purchases_by_supplier' },
    next: (r) => ({ title: `${r.supplier} — purchase invoices`, q: { kind: 'purchases', supplier_id: r.supplier_id || undefined } }),
  },
  opex: (m) => ({ title: `${m} — transactions`, q: { kind: 'opex', key: m } }),
  outstanding: {
    title: 'Supplier outstanding', q: { kind: 'supplier_outstanding' },
    next: (r) => ({ title: `${r.supplier} — unpaid invoices`, q: { kind: 'supplier_bills', supplier_id: r.supplier_id } }),
  },
  interbranch: { title: 'Interbranch — branch pairs (before settlements)', q: { kind: 'interbranch' } },
};

const PnlBox = ({ d, open }) => {
  const p = d.profit_and_loss;
  return (
    <Box title="Profit & loss (operational, excl. VAT)" testid="pap-pnl">
      <Row label="Net sales" value={p.net_sales} testid="pap-net-sales" />
      <Row label="Cost of sales (purchases)" value={p.cost_of_sales} onClick={() => open(D.purchases)} testid="pap-cost-of-sales" />
      <Row label="Gross profit" value={p.gross_profit} strong testid="pap-gross-profit" />
      {Object.entries(p.operating_expenses).map(([m, v]) => (
        <Row key={m} label={m} value={v} onClick={() => open(D.opex(m))} testid={`pap-opex-${m.toLowerCase().replace(/\W+/g, '-')}`} />
      ))}
      <Row label="Net profit" value={p.net_profit} strong testid="pap-net-profit" />
    </Box>
  );
};

const PayableBox = ({ s, open }) => (
  <Box title="Supplier payments & outstanding" testid="pap-payable">
    <Row label="Opening payable" value={s.opening_payable} />
    <Row label="New credit bills" value={s.new_credit_bills} />
    <Row label="Paid — previous-period dues" value={s.paid_against_previous_credit} testid="pap-paid-previous" />
    <Row label="Paid — this period's bills" value={s.paid_against_current_credit} testid="pap-paid-current" />
    <Row label="Advances applied / returns" value={(s.advances_applied_to_bills || 0) + (s.returns_credit_notes || 0)} />
    <Row label="Closing supplier outstanding" value={s.closing_payable} strong onClick={() => open(D.outstanding)} testid="pap-closing-payable" />
    <Row label="Supplier advances (paid ahead)" value={s.closing_advances} testid="pap-closing-advances" />
    <Row label="Paid by selected branch(es)" value={s.total_paid} testid="pap-total-paid" />
    {s.unlinked_supplier_payment_expenses?.count > 0 && (
      <Row label={`Payments with no payment record (${s.unlinked_supplier_payment_expenses.count})`} value={s.unlinked_supplier_payment_expenses.amount} testid="pap-unlinked" />
    )}
  </Box>
);

const CashBox = ({ c, ib, open }) => (
  <Box title="Cash & bank (paying branch)" testid="pap-cash-bank">
    <Row label="Received cash" value={c.received_cash} /><Row label="Received bank / card" value={c.received_bank_card} />
    <Row label="Paid cash" value={c.paid_cash} testid="pap-paid-cash" /><Row label="Paid bank" value={c.paid_bank} testid="pap-paid-bank" />
    <Row label="Net cash & bank movement" value={c.net_cash_bank_movement} strong testid="pap-net-cash-bank" />
    <Row label="Paid by other branches for us" value={ib.paid_by_other_branches_for_us} onClick={() => open(D.interbranch)} testid="pap-ib-for-us" />
    <Row label="We paid for other branches" value={ib.we_paid_for_other_branches} onClick={() => open(D.interbranch)} testid="pap-ib-we-paid" />
  </Box>
);

const OtherBox = ({ d, open }) => {
  const g = d.posted_gl_comparison || {};
  return (
    <Box title="Purchases, VAT & posted GL" testid="pap-purchases">
      <Row label="Purchases incl. VAT" value={d.purchases.total_new_purchases_gross} onClick={() => open(D.purchases)} testid="pap-purchases-gross" />
      <Row label="Input VAT (purchases + expenses)" value={d.input_vat} testid="pap-input-vat" />
      <Row label="On credit (incl. VAT)" value={d.purchases.credit_purchases} />
      <Row label="Partner / loans / transfers" value={d.partner_loans_transfers.total} />
      <Row label="VAT / GOSI / payable settlements" value={d.liability_settlements.total} />
      {g.posted_gl_cost_of_sales !== undefined && <Row label="Posted GL cost of sales" value={g.posted_gl_cost_of_sales} testid="pap-gl-cogs" />}
      <Row label="Purchases posted to payable (File 29)" value={g.supplier_purchases_posted_to_payable} testid="pap-gl-misposted" />
    </Box>
  );
};

// Shared period view (same rules as Branch Summary / P&L): purchases, settlements, payables, cash/bank, interbranch.
export const PeriodAccountingPanel = ({ start, end, branchId, branchIds }) => {
  const [d, setD] = useState(null);
  const [drill, setDrill] = useState(null);
  const params = { start_date: start, end_date: end, ...(branchId ? { branch_id: branchId } : {}), ...(branchIds?.length ? { branch_ids: branchIds.join(',') } : {}) };
  useEffect(() => {
    if (!start || !end) { setD(null); return; }
    api.get('/accounting/period-summary', { params }).then((r) => setD(r.data)).catch(() => setD(null));
  }, [start, end, branchId, branchIds?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  return (
    <div className="mb-4" data-testid="period-accounting-panel">
      <div className="flex justify-end mb-1">
        <ExportTrio spec={() => flattenSpec('Period accounting summary', { ...d, period: undefined }, `${start} – ${end}`)} testId="pap-export" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
        <PnlBox d={d} open={setDrill} />
        <PayableBox s={d.supplier_payable} open={setDrill} />
        <CashBox c={d.cash_bank} ib={d.interbranch} open={setDrill} />
        <OtherBox d={d} open={setDrill} />
      </div>
      <PeriodDrilldown req={drill} params={params} onClose={() => setDrill(null)} onOpen={setDrill} />
    </div>
  );
};
